<template>
  <!-- model-value null on purpose: the product shows in the label, not the value. -->
  <RevInputSelectSearchable
    :id
    hide-checkboxes
    :label="cleanLabel"
    :model-value="null"
    :no-options-message="i18n(translations.noProductFound)"
    :options="cleanOptions"
    search-placement="dropdown"
    @update:model-value="onSelect"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import {
  type Option,
  RevInputSelectSearchable,
} from '@ds/components/InputSelectSearchable'

import translations from './ProductSwitcher.translations'

const props = defineProps<{
  id: string
  label: string
  options: { id: string; label: string; value: string }[]
}>()

const emit = defineEmits<{
  switch: [option: { value: string }]
}>()

const i18n = useI18n()

// Strip the variant suffix that comes after "•" (e.g. "iPhone 11 Pro Max • Unlocked"
// → "iPhone 11 Pro Max") so long product names stay compact in the dropdown.
function stripVariantSuffix(name: string) {
  return name.split('•')[0]?.trim() ?? name
}

const cleanOptions = computed(() =>
  props.options.map((opt) => ({
    ...opt,
    label: stripVariantSuffix(opt.label),
  })),
)

// Optimistic label so the switch feels instant; the parent nav settles it later.
const pickedLabel = ref<string | null>(null)
watch(
  () => props.label,
  () => {
    pickedLabel.value = null
  },
)
const cleanLabel = computed(() =>
  stripVariantSuffix(pickedLabel.value ?? props.label),
)

function onSelect(opt: Option | null) {
  if (opt && 'value' in opt) {
    pickedLabel.value = opt.label
    emit('switch', { value: String(opt.value) })
  }
}
</script>
